import { Spinner } from './spinner'

/**
 * `LoadingPanel` - the in-card "fetching a record" state.
 *
 * One of two loading treatments, and the boundary between them is:
 *
 *   `SkeletonRows`  a list whose shape is known - draw the shape.
 *   `LoadingPanel`  a single record whose shape is not yet known.
 *
 * There used to be three: skeleton rows on the list, a bare "Loading..."
 * string on the detail page, and "Loading holder..." inside a card. Three
 * vocabularies for one idea, on screens a user moves between in a second.
 *
 * `role="status"` with `aria-live="polite"` so the wait is announced
 * without interrupting; the spinner is decorative and hidden.
 */
export interface LoadingPanelProps {
  /** Announced and displayed. Sentence case, no trailing ellipsis. */
  readonly label: string
  /** Compact variant for use inside an existing card. */
  readonly inline?: boolean
}

export function LoadingPanel({ label, inline = false }: LoadingPanelProps): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        'flex items-center justify-center gap-murmuration-2 text-murmuration-body text-text-muted',
        inline
          ? 'py-murmuration-3'
          : 'rounded-md border border-border-default bg-bg-surface px-murmuration-5 py-murmuration-7',
      ].join(' ')}
    >
      <Spinner />
      <span>{label}</span>
    </div>
  )
}
